import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import React from 'react'

import type { SchemaCampaignQuota, SchemaQuotaAdd } from '@/__generated/es-api'
import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import type { Condition, PersonalizationType } from '@/app/types'

import {
  areEditableQuotaConditionsValid,
  fromEditableQuotaConditions,
  toEditableQuotaConditions,
} from './quota-conditions'
import { SingleQuotaForm } from './quota-form'
import type { QuotaMetadataFormValues } from './quota-form-validation'

type Props = {
  isCreating: boolean
  personalizationList: PersonalizationType
  referenceQuota?: SchemaCampaignQuota
  onClose: () => void
  onCreate: (quota: SchemaQuotaAdd, conditions: Condition | null) => Promise<boolean>
}

const CREATE_QUOTA_FORM_ID = 'create-quota-form'

const QuotaCreateDialog: React.FC<Props> = ({ isCreating, personalizationList, referenceQuota, onClose, onCreate }) => {
  const editableConditions = React.useMemo(
    () => toEditableQuotaConditions(referenceQuota?.conditions ?? []),
    [referenceQuota?.conditions],
  )
  const [conditionsValidationRequested, setConditionsValidationRequested] = React.useState(false)
  const [conditionGroups, setConditionGroups] = React.useState(editableConditions.groups)
  const defaultValues = React.useMemo(
    () => ({
      goal: referenceQuota ? String(referenceQuota.quotaGoal) : '',
      name: referenceQuota ? `Copy of ${referenceQuota.quotaName}` : '',
    }),
    [referenceQuota],
  )
  const conditionsValid = !editableConditions.supported || areEditableQuotaConditionsValid(conditionGroups)

  const handleCreate = async ({ goal, name }: QuotaMetadataFormValues) => {
    setConditionsValidationRequested(true)
    if (!conditionsValid) {
      return
    }

    const conditions = editableConditions.supported
      ? fromEditableQuotaConditions(conditionGroups)
      : (referenceQuota?.conditions as Condition | null)
    const success = await onCreate({ goal: Number(goal), name }, conditions)
    if (success) {
      onClose()
    }
  }

  return (
    <Dialog fullWidth maxWidth="md" open onClose={isCreating ? undefined : onClose}>
      <DialogHeader
        closeButtonLabel="Close create quota dialog"
        disabled={isCreating}
        title="Create new quota"
        onClose={onClose}
      />
      <DialogContent>
        <SingleQuotaForm
          conditionGroups={conditionGroups}
          conditionsError={conditionsValidationRequested}
          conditionsSupported={editableConditions.supported}
          defaultValues={defaultValues}
          id={CREATE_QUOTA_FORM_ID}
          metadataDisabled={isCreating}
          personalizationList={personalizationList}
          onConditionsChange={setConditionGroups}
          onSubmit={handleCreate}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: conditionGroups.length ? 'space-between' : 'flex-end' }}>
        {conditionGroups.length > 0 && (
          <Button color="error" disabled={isCreating} onClick={() => setConditionGroups([])}>
            Remove all conditions
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button disabled={isCreating} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={isCreating}
            form={CREATE_QUOTA_FORM_ID}
            loading={isCreating}
            type="submit"
            variant="contained"
            onClick={() => setConditionsValidationRequested(true)}
          >
            Create quota
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default QuotaCreateDialog
